import { AppStateEnum } from '../../../main/types/app-state.types.ts';
import { getAudioRecordingUtil } from '../../../main/utils/audio/audio-recording.util.ts';
import { getLocaleUtil } from '../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../main/utils/monitoring/sentry.util.ts';
import { getReduxStoreUtil } from '../../../main/utils/redux-store.util.ts';

import { getPronunciationGraderParametersQueryRequest } from '../../requests/get-pronunciation-grader-parameters.query.ts';
import { updateAppStateThunk } from '../../slices/app-state/thunks/update-app-state.thunk.ts';
import {
  BasePronunciationGraderInterface,
  PronunciationGradingResult,
  PronunciationGraderEntry,
  PronunciationGraderState,
  BasePronunciationGraderResultDetails,
} from '../../types/pronunciation-grader.types.ts';
import {
  GraderType,
  LetterNameParameters,
  LetterNameParametersSchema,
} from '../../types/pronunciation-grading-parameters.types.ts';

import { SpeechSuperGradingUtil } from '../speechsuper/speechsuper-grading.util.ts';

/**
 * Handles grading of letter name pronunciation.
 * Uses SpeechSuper for real-time grading of letter name pronunciation.
 */
export class LetterNamePronunciationGrader implements BasePronunciationGraderInterface {
  private static _instance: LetterNamePronunciationGrader;

  static get instance(): LetterNamePronunciationGrader {
    if (!LetterNamePronunciationGrader._instance) {
      LetterNamePronunciationGrader._instance = new LetterNamePronunciationGrader();
    }
    return LetterNamePronunciationGrader._instance;
  }

  private constructor(private audioRecorder = getAudioRecordingUtil()) {}

  private entry: PronunciationGraderEntry<LetterNameParameters> | null = null;

  private state: PronunciationGraderState = {
    isInitialized: false,
    isRecording: false,
    isGrading: false,
  };

  /**
   * Initializes the grader with the reference text to check against.
   * Must be called before starting grading.
   * @param refText - The reference text to grade against
   */
  async initialize(refText: string): Promise<void> {
    if (this.state.isRecording || this.state.isGrading) {
      throw new Error(getLocaleUtil().select('learn:grader_busy'));
    }

    // Get grading parameters
    const result = await getPronunciationGraderParametersQueryRequest({
      graderType: GraderType.LETTER_NAME,
      refText,
    });

    if (!result.success || !result.data) {
      throw new Error(getLocaleUtil().select('learn:grader_initialization_failed'));
    }

    const parameters = LetterNameParametersSchema.parse(result.data.parameters);
    const grader = new SpeechSuperGradingUtil();
    grader.initialize({
      graderType: GraderType.LETTER_NAME,
      refText: parameters.letter,
    });
    // Initialize grader for letter name
    this.entry = {
      grader,
      parameters,
      isCorrect: true,
    };

    this.state.isInitialized = true;
  }

  /**
   * Starts the grading process.
   * Must be called after initialize.
   * @returns A promise that resolves with the grading result
   */
  async startGrading(): Promise<PronunciationGradingResult<BasePronunciationGraderResultDetails>> {
    if (!this.state.isInitialized || !this.entry) {
      throw new Error(getLocaleUtil().select('learn:grader_not_initialized'));
    }

    if (this.state.isRecording || this.state.isGrading) {
      throw new Error(getLocaleUtil().select('learn:grader_busy'));
    }

    // Start the grader
    await this.entry.grader.startGrading();
    this.state.isGrading = true;

    return new Promise((resolve, reject) => {
      // Start recording with real-time processing
      this.audioRecorder
        .record({
          callbacks: {
            onPartialResult: async (audioData) => {
              await this.entry?.grader.sendAudioData(audioData);
            },
          },
          silenceDetection: {
            enabled: true,
            onSilenceDetected: async () => {
              const result = await this.finalizeGrading();
              resolve({
                isCorrect: result.isCorrect,
                details: { recognizedText: this.entry?.parameters.letter ?? '' },
              });
            },
          },
        })
        .catch(reject);
    });
  }

  /**
   * Aborts the grading process.
   * @returns A promise that resolves when the grading is aborted
   */
  async abort(): Promise<void> {
    const isRecording = this.state.isRecording;
    const isGrading = this.state.isGrading;
    const grader = this.entry?.grader;
    this.cleanup();

    if (isRecording) {
      await this.audioRecorder.stopRecording();
    }
    if (isGrading && grader) {
      await grader.abort();
    }
  }

  private async finalizeGrading(): Promise<PronunciationGradingResult<void>> {
    try {
      if (!this.entry) {
        throw new Error(getLocaleUtil().select('learn:grader_not_initialized'));
      }

      const dispatch = await getReduxStoreUtil().getDispatch();
      await dispatch(updateAppStateThunk({ state: AppStateEnum.Evaluating }));

      // Get result from grader
      const result = await this.entry.grader.stopGrading();
      if (!result) {
        throw new Error(getLocaleUtil().select('learn:evaluation_timeout'));
      }
      const overall = result.result.overall;

      // Return true if score is above threshold
      const isCorrect = overall >= this.entry.parameters.threshold;
      return { isCorrect };
    } catch (error) {
      getSentryUtil().trackError(error as Error, {
        grader: 'letter_name',
        refText: this.entry?.parameters.letter ?? undefined,
      });
      return { isCorrect: false };
    } finally {
      this.cleanup();
    }
  }

  private cleanup(): void {
    this.state.isRecording = false;
    this.state.isGrading = false;
    this.state.isInitialized = false;
    this.entry = null;
  }
}

export const getLetterNamePronunciationGrader = (): LetterNamePronunciationGrader =>
  LetterNamePronunciationGrader.instance;
